/**
 * Générateur — Attendu 655 — Connaître les définitions d'un cercle, d'un disque,
 * d'un rayon, d'un diamètre, d'une corde.
 * Niveau : 6ème | Cluster : 30 | Domaine : Espace et géométrie
 *
 * 5 exercices / 5 formats :
 *   - vocabulaire_cercle_vf_figure     : V/F (3V+3F stricte) sur figure SVG d'un cercle
 *   - lexique_cercle_qcm               : QCM 4 choix sur la définition d'un terme parmi 5
 *   - rayon_diametre_saisie            : saisie r ↔ D (4 régimes : r→D entier, D→r entier,
 *                                         D→r décimal, r (cm) → D (mm))
 *   - trou_vocabulaire_qcm             : QCM 4 options — phrase à trou (5 structures)
 *   - construction_cercles_programme   : programme_construction (3 tâches : rayon num,
 *                                         rayon symbolique, diamètre symbolique)
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_655.md et docs/fiches/PAYLOADS_655.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 655;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_VOCAB_VF = 'Vocabulaire cercle (Vrai/Faux sur figure)';
const L_LEXIQUE = 'Lexique cercle/disque (QCM)';
const L_RAYON_DIAMETRE = 'Rayon ↔ Diamètre (saisie)';
const L_TROU_VOCAB = 'Texte à trous (QCM)';
const L_CONSTRUCTION = 'Construction de cercles';

// Lettres valides pour nommer les points (exclut I et O : ambiguïté avec 1 et 0).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// Constantes SVG (programme_construction — alignées avec gen_patron.js / gen_654)
const PX_PER_CM = 30;
const VIEWBOX_PC = { w: 600, h: 400 };
const TOLERANCE_PC = 12;

// Couleurs pour les tâches (programme_construction)
const COLOR_BLEU = '#378ADD';
const COLOR_ROUGE = '#E24B4A';
const COLOR_VERT = '#1D9E75';

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Formate un nombre à la française (virgule décimale). */
const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    return String(n).replace('.', ',');
};

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/** Distance euclidienne entre deux points {x,y}. */
const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Vocabulaire V/F sur figure (cercle + 4 points nommés)
// ═══════════════════════════════════════════════════════════

/** Pool des affirmations VRAIES (dépend des noms tirés A, B, C, D + centre O_name). */
const POOL_VRAIES_VF = (A, B, C, D, Ocenter) => [
    `[${Ocenter}${A}] est un rayon du cercle`,
    `[${A}${B}] est un diamètre du cercle`,
    `[${C}${D}] est une corde du cercle`,
    `Tout diamètre de ce cercle est aussi une corde`,
    `Le centre ${Ocenter} n'appartient pas au cercle`,
    `${Ocenter}${A} = ${Ocenter}${B}`,
];

/** Pool des affirmations FAUSSES. */
const POOL_FAUSSES_VF = (A, B, C, D, Ocenter) => [
    `[${C}${D}] est un diamètre du cercle`,                         // corde non-diamètre par construction
    `[${Ocenter}${C}] est une corde du cercle`,                     // O ∉ cercle → ce n'est pas une corde
    `Le centre ${Ocenter} appartient au cercle`,                    // confusion cercle/centre
    `Toute corde d'un cercle est un diamètre`,                      // généralisation fausse
    `Le disque de centre ${Ocenter} et de rayon r se réduit au cercle`, // confusion cercle/disque
    `Un cercle peut avoir deux centres différents`,                 // unicité du centre
];

/**
 * Construit le SVG de la figure : cercle + centre + 4 points (A, B diamétralement
 * opposés ; C, D non-antipodaux) + segments [AB] (diamètre), [CD] (corde), [OA] (rayon).
 */
const buildFigureVFSvg = (A, B, C, D, Ocenter, rCm, thetaBase, alphaCOffset, alphaD) => {
    // viewBox 260 × 240 et non 240 × 200 : les libellés sont poussés 12 px À
    // L'EXTÉRIEUR du cercle, or R monte à 100 px (rCm = 4 × 25). Avec l'ancien
    // cadre, un point situé en bas plaçait sa lettre à y = 216 pour une hauteur
    // de 200 — tronquée. 30,4 % des tirages étaient concernés. Le cadre est
    // mis à l'échelle par le CSS : l'agrandir n'a pas rapetissé la figure.
    // Pire cas désormais : 120 + 100 + 16 = 236 ≤ 240.
    const cx = 130;
    const cy = 120;
    const R = rCm * 25; // 25 px / cm sur la figure illustrative
    const Ax = cx + R * Math.cos(thetaBase);
    const Ay = cy + R * Math.sin(thetaBase);
    const Bx = cx + R * Math.cos(thetaBase + Math.PI);
    const By = cy + R * Math.sin(thetaBase + Math.PI);
    const alphaC = thetaBase + alphaCOffset;
    const Cx = cx + R * Math.cos(alphaC);
    const Cy = cy + R * Math.sin(alphaC);
    const Dx = cx + R * Math.cos(alphaD);
    const Dy = cy + R * Math.sin(alphaD);

    // Décalage de label : on pousse le texte légèrement à l'extérieur du cercle
    const labelOffset = 12;
    const labelPos = (px, py) => {
        const vx = px - cx;
        const vy = py - cy;
        const L = Math.hypot(vx, vy) || 1;
        return {
            x: (px + (vx / L) * labelOffset).toFixed(1),
            y: (py + (vy / L) * labelOffset + 4).toFixed(1),
        };
    };
    const lA = labelPos(Ax, Ay);
    const lB = labelPos(Bx, By);
    const lC = labelPos(Cx, Cy);
    const lD = labelPos(Dx, Dy);

    const body =
        `<circle cx="${cx}" cy="${cy}" r="${R.toFixed(1)}" stroke="#000" fill="none" stroke-width="2" />`
        + `<circle cx="${cx}" cy="${cy}" r="2" fill="#000" />`
        + `<line x1="${Ax.toFixed(1)}" y1="${Ay.toFixed(1)}" x2="${Bx.toFixed(1)}" y2="${By.toFixed(1)}" stroke="#000" stroke-width="1.5" />`
        + `<line x1="${Cx.toFixed(1)}" y1="${Cy.toFixed(1)}" x2="${Dx.toFixed(1)}" y2="${Dy.toFixed(1)}" stroke="#000" stroke-width="1.5" />`
        + `<line x1="${cx}" y1="${cy}" x2="${Ax.toFixed(1)}" y2="${Ay.toFixed(1)}" stroke="#000" stroke-width="1.5" />`
        + `<circle cx="${Ax.toFixed(1)}" cy="${Ay.toFixed(1)}" r="2.5" fill="#000" />`
        + `<circle cx="${Bx.toFixed(1)}" cy="${By.toFixed(1)}" r="2.5" fill="#000" />`
        + `<circle cx="${Cx.toFixed(1)}" cy="${Cy.toFixed(1)}" r="2.5" fill="#000" />`
        + `<circle cx="${Dx.toFixed(1)}" cy="${Dy.toFixed(1)}" r="2.5" fill="#000" />`
        + `<text x="${lA.x}" y="${lA.y}" font-size="14" fill="#000">${A}</text>`
        + `<text x="${lB.x}" y="${lB.y}" font-size="14" fill="#000">${B}</text>`
        + `<text x="${lC.x}" y="${lC.y}" font-size="14" fill="#000">${C}</text>`
        + `<text x="${lD.x}" y="${lD.y}" font-size="14" fill="#000">${D}</text>`
        + `<text x="${cx - 12}" y="${cy + 4}" font-size="14" fill="#000">${Ocenter}</text>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 240">${body}</svg>`;
};

const genVocabulaireVFFigure = () => {
    // Tirage de 5 lettres distinctes : A, B, C, D + centre (Ocenter)
    const [A, B, C, D, Ocenter] = pickN(5);

    // Géométrie de la figure
    const rCm = randomChoice([2, 2.5, 3, 3.5, 4]);
    const thetaBase = Math.random() * 2 * Math.PI;
    // αC décalé de ~2π/5 à ~π/2 par rapport à A (évite A, B)
    const alphaCOffset = Math.PI * (0.35 + Math.random() * 0.25); // ≈ 63° à 108°
    // αD décalé de αC de [2π/5, 4π/5] pour garantir non-antipodaux
    const deltaCD = Math.PI * (0.40 + Math.random() * 0.40); // ≈ 72° à 144°
    const alphaD = thetaBase + alphaCOffset + deltaCD;

    const svg = buildFigureVFSvg(A, B, C, D, Ocenter, rCm, thetaBase, alphaCOffset, alphaD);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    // Composition 3V + 3F (stricte)
    const vraies = POOL_VRAIES_VF(A, B, C, D, Ocenter);
    const fausses = POOL_FAUSSES_VF(A, B, C, D, Ocenter);
    const chosenVraies = randomChoiceMultiple(vraies, 3);
    const chosenFausses = randomChoiceMultiple(fausses, 3);

    // SHUFFLE côté générateur (le helper ne mélange pas)
    const merged = shuffle([
        ...chosenVraies.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenFausses.map(l => ({ ligne: l, reponse: 1 })),
    ]);

    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    // Deux phrases courtes : l'appartenance des 4 points AU CERCLE conditionne
    // plusieurs affirmations (une corde joint deux points du cercle), elle mérite
    // sa propre phrase plutôt qu'une relative. La consigne de cochage saute — les
    // colonnes Vrai/Faux la disent déjà. `{{element:0}}` place la figure
    // délibérément : sans marqueur elle n'était rendue qu'en repli, après le texte.
    const texte = `<p>${Ocenter} est le centre du cercle ci-dessous. ${A}, ${B}, ${C} et ${D} sont des points de ce cercle.</p>{{element:0}}`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_VOCAB_VF),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'vocabulaire_cercle_vf_figure';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — QCM lexique (définition pure, sans figure)
// ═══════════════════════════════════════════════════════════

/** Définitions correctes par terme. */
const DEFINITIONS = {
    cercle:   `L'ensemble des points situés à la même distance d'un point appelé centre.`,
    disque:   `L'ensemble des points situés à une distance inférieure ou égale à r d'un point appelé centre.`,
    rayon:    `Un segment joignant le centre à un point du cercle, ou la longueur de ce segment.`,
    diametre: `Un segment joignant deux points du cercle et passant par le centre, ou la longueur de ce segment.`,
    corde:    `Un segment joignant deux points du cercle.`,
};

/** Libellés affichés (le terme est mis en gras dans la question). */
const TERME_LABEL = {
    cercle: 'cercle',
    disque: 'disque',
    rayon: 'rayon',
    diametre: 'diamètre',
    corde: 'corde',
};

/**
 * Article de chaque terme. Le genre est connu au moment de construire la
 * question : écrire « d'un(e) » revenait à faire porter à l'élève une notation
 * administrative qui n'a pas sa place dans un énoncé de 6e.
 */
const TERME_ARTICLE = {
    cercle: 'un',
    disque: 'un',
    rayon: 'un',
    diametre: 'un',
    corde: 'une',
};

/** Distracteurs ciblés par terme (confusions typiques, ≥ 6 par terme pour la variété). */
const DISTRACTEURS_LEXIQUE = {
    cercle: [
        DEFINITIONS.disque,                                                // confusion cercle/disque
        DEFINITIONS.rayon,
        DEFINITIONS.corde,
        DEFINITIONS.diametre,
        `Un segment qui passe par le centre d'une figure.`,
        `La longueur du contour d'une figure ronde.`,
        `L'intérieur d'une figure ronde, sans son contour.`,
    ],
    disque: [
        DEFINITIONS.cercle,                                                // confusion disque/cercle
        DEFINITIONS.rayon,
        DEFINITIONS.diametre,
        `Seulement le bord d'une figure ronde, sans son intérieur.`,
        `Un segment égal au double du rayon.`,
        `L'ensemble des points à l'extérieur d'un cercle.`,
        `Un cercle auquel on ajoute ses deux diamètres.`,
    ],
    rayon: [
        DEFINITIONS.corde,
        DEFINITIONS.diametre,                                              // confusion rayon/diamètre
        DEFINITIONS.cercle,
        `La longueur totale du contour d'un cercle.`,
        `Un segment joignant deux points du cercle et passant par le centre.`,
        `La distance la plus grande entre deux points d'un cercle.`,
        `Un segment qui touche le cercle en un seul point.`,
    ],
    diametre: [
        DEFINITIONS.rayon,                                                 // confusion diamètre/rayon
        DEFINITIONS.corde,
        DEFINITIONS.disque,
        `La distance du centre à un point du disque.`,
        `La moitié de la longueur du cercle.`,
        `Un segment dont une extrémité est le centre.`,
        `La plus petite corde d'un cercle.`,
    ],
    corde: [
        DEFINITIONS.diametre,                                              // corde n'est pas toujours un diamètre
        DEFINITIONS.rayon,
        DEFINITIONS.cercle,
        `Un segment égal à la moitié du rayon.`,
        `Un segment passant obligatoirement par le centre du cercle.`,
        `Un segment reliant le centre à un point du cercle.`,
        `Un arc de cercle compris entre deux points.`,
    ],
};

const genLexiqueCercleQcm = () => {
    const termeKey = randomChoice(Object.keys(DEFINITIONS));
    const bonne = DEFINITIONS[termeKey];

    // 3 distracteurs distincts et ≠ bonne
    const poolDistract = DISTRACTEURS_LEXIQUE[termeKey].filter(d => d !== bonne);
    const distracteurs = randomChoiceMultiple(poolDistract, 3);

    const choix = [bonne, ...distracteurs];
    const texte = `<p>Quelle est la définition d'${TERME_ARTICLE[termeKey]} <strong>${TERME_LABEL[termeKey]}</strong> ?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_LEXIQUE));
    payload._meta.generatorKey = 'lexique_cercle_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Conversion rayon ↔ diamètre (saisie numérique)
// ═══════════════════════════════════════════════════════════

const genRayonDiametreSaisie = () => {
    const [Centre] = pickN(1);
    const regime = randomChoice(['r_vers_D_entier', 'D_vers_r_entier', 'D_vers_r_decimal', 'r_cm_vers_D_mm']);

    let texte, bonneReponse, unite;

    if (regime === 'r_vers_D_entier') {
        // r entier 2..15 → D = 2r
        const r = randInt(2, 15);
        const D = 2 * r;
        texte = `<p>Un cercle a pour centre ${Centre} et pour rayon ${r} cm.</p><p>Quel est son diamètre, en cm ?</p>`;
        bonneReponse = fmt(D);
        unite = 'cm';
    } else if (regime === 'D_vers_r_entier') {
        // D pair 4..30 → r = D/2 entier
        const D = randomChoice([4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30]);
        const r = D / 2;
        texte = `<p>Un cercle a pour centre ${Centre} et pour diamètre ${D} cm.</p><p>Quel est son rayon, en cm ?</p>`;
        bonneReponse = fmt(r);
        unite = 'cm';
    } else if (regime === 'D_vers_r_decimal') {
        // D impair 3..29 → r = D/2 demi-entier
        const D = randomChoice([3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29]);
        const r = D / 2;
        texte = `<p>Un cercle a pour centre ${Centre} et pour diamètre ${D} cm.</p><p>Quel est son rayon, en cm ?</p>`;
        bonneReponse = fmt(r);
        unite = 'cm';
    } else {
        // r_cm_vers_D_mm : r entier 2..15 → D en mm = 2r × 10 = 20r
        const r = randInt(2, 15);
        const D_mm = Math.round(2 * r * 10); // arrondi explicite (sécurité flottants)
        texte = `<p>Un cercle a pour centre ${Centre} et pour rayon ${r} cm.</p><p>Quel est son diamètre, en <strong>mm</strong> ?</p>`;
        bonneReponse = fmt(D_mm);
        unite = 'mm';
    }

    const payload = makeSaisie(bonneReponse, texte, {
        ...opts(L_RAYON_DIAMETRE),
        suffixe: ` ${unite}`,
    });
    payload._meta.generatorKey = 'rayon_diametre_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Texte à trous (QCM avec mot à choisir)
// ═══════════════════════════════════════════════════════════

/**
 * Structures de phrase — 6 variantes. Chaque fonction renvoie {phrase, bonne}.
 * Le placeholder `${C}` est remplacé par une lettre tirée dans LETTRES (≠ I, O).
 *
 * ⚠ AUCUN ARTICLE avant le trou — d'où « porte le nom de » et non « s'appelle
 * un/une ». Avec l'article dans la phrase, « s'appelle une ____ » annonçait le
 * genre, et `corde` étant le seul mot féminin du pool, l'élève cochait juste
 * sans connaître un mot de géométrie (19,8 % des tirages mesurés ; 69 %
 * perdaient au moins une option par élimination grammaticale). Déplacer
 * l'article dans les options ne suffirait pas : `la corde` reste aussi seule de
 * son genre que `une corde`. La seule forme neutre est de n'en montrer aucun.
 */
const PHRASE_STRUCTURES = [
    (C) => ({
        phrase: `Sur un cercle de centre ${C}, un segment reliant ${C} à un point du cercle porte le nom de ____.`,
        bonne: 'rayon',
    }),
    (C) => ({
        phrase: `Sur un cercle de centre ${C}, un segment qui joint deux points du cercle et qui passe par ${C} porte le nom de ____.`,
        bonne: 'diamètre',
    }),
    (C) => ({
        phrase: `Sur un cercle de centre ${C}, un segment qui joint deux points du cercle sans forcément passer par ${C} porte le nom de ____.`,
        bonne: 'corde',
    }),
    (C) => ({
        phrase: `La figure formée par tous les points situés à une distance inférieure ou égale à r d'un point ${C} porte le nom de ____.`,
        bonne: 'disque',
    }),
    (C) => ({
        phrase: `La figure formée par tous les points situés exactement à la distance r d'un point ${C} porte le nom de ____.`,
        bonne: 'cercle',
    }),
    // 6e structure : sans elle, `centre` figurait dans 6 tirages sur 10 comme
    // distracteur sans jamais pouvoir être la bonne réponse.
    (C) => ({
        phrase: `Sur un cercle, le point ${C} situé à égale distance de tous les points du cercle porte le nom de ____.`,
        bonne: 'centre',
    }),
];

const POOL_MOTS_TROU = ['cercle', 'disque', 'rayon', 'diamètre', 'corde', 'centre'];

const genTrouVocabulaireQcm = () => {
    const [C] = pickN(1); // lettre centre (hors I, O)
    const structFn = randomChoice(PHRASE_STRUCTURES);
    const { phrase, bonne } = structFn(C);

    // 3 distracteurs dans POOL_MOTS_TROU \ {bonne}
    const poolDistract = POOL_MOTS_TROU.filter(m => m !== bonne);
    const distracteurs = randomChoiceMultiple(poolDistract, 3);
    const choix = [bonne, ...distracteurs];

    // Pas de « Complète la phrase suivante avec le bon mot. » : le trou et les
    // quatre options le disent déjà.
    const texte = `<p><em>${phrase}</em></p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_TROU_VOCAB));
    payload._meta.generatorKey = 'trou_vocabulaire_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Construction de cercles (programme_construction)
// ═══════════════════════════════════════════════════════════

/**
 * Tire 6 points {name, x, y} aléatoirement dans le viewBox, avec contraintes :
 *   - marge ≥ 60 px des bords
 *   - distance deux-à-deux ≥ 55 px
 *   - distance P3P4 ∈ [60, 180] px (pour T2 : rayon symbolique)
 *   - distance P5P6 ∈ [90, 210] px (pour T3 : diamètre)
 */
const tirer6PointsConstruction = (r1Px) => {
    const names = pickN(6);
    const margin = 60;
    const W = VIEWBOX_PC.w;
    const H = VIEWBOX_PC.h;
    const maxAttempts = 400;
    const bboxMargin = 10;
    // Un cercle (cx, cy, r) doit tenir dans le viewBox avec la marge
    const cercleOk = (cx, cy, r) =>
        cx - r >= bboxMargin && cx + r <= W - bboxMargin
        && cy - r >= bboxMargin && cy + r <= H - bboxMargin;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const pts = names.map(name => ({
            name,
            x: randInt(margin, W - margin),
            y: randInt(margin, H - margin),
        }));
        // Distance deux-à-deux ≥ 55 px
        let ok = true;
        for (let i = 0; i < 6 && ok; i++) {
            for (let j = i + 1; j < 6 && ok; j++) {
                if (dist(pts[i], pts[j]) < 55) ok = false;
            }
        }
        if (!ok) continue;
        // Distance P3P4 ∈ [60, 180]
        const d34 = dist(pts[2], pts[3]);
        if (d34 < 60 || d34 > 180) continue;
        // Distance P5P6 ∈ [90, 210]
        const d56 = dist(pts[4], pts[5]);
        if (d56 < 90 || d56 > 210) continue;
        // Les TROIS cercles doivent tenir dans le viewBox :
        // T1 (centre P1, r1), T2 (centre P2, rayon P3P4), T3 (diamètre [P5P6])
        if (!cercleOk(pts[0].x, pts[0].y, r1Px)) continue;
        if (!cercleOk(pts[1].x, pts[1].y, d34)) continue;
        const milieu = { x: (pts[4].x + pts[5].x) / 2, y: (pts[4].y + pts[5].y) / 2 };
        if (!cercleOk(milieu.x, milieu.y, d56 / 2)) continue;
        // Aucun point nommé à moins de 25 px du milieu de [P5P6] : un point dans
        // la zone de snap (12-22 px) capturerait le clic et rendrait le centre
        // du cercle T3 inatteignable
        if (pts.some(p => dist(p, milieu) < 25)) continue;
        return pts;
    }
    // Fallback déterministe validé (r1 max 120, d34 ≈ 92, d56 = 120 → tous
    // les cercles dans [10;590]×[10;390], écarts deux-à-deux ≥ 55)
    return [
        { name: names[0], x: 200, y: 200 },
        { name: names[1], x: 430, y: 180 },
        { name: names[2], x: 60,  y: 320 },
        { name: names[3], x: 150, y: 340 },
        { name: names[4], x: 300, y: 90 },
        { name: names[5], x: 420, y: 90 },
    ];
};

const genConstructionCerclesProgramme = () => {
    // T1 : rayon numérique (tiré AVANT le placement pour valider le viewBox)
    const r1Cm = randomChoice([2, 3, 4]);
    const r1Px = r1Cm * PX_PER_CM;

    const pts = tirer6PointsConstruction(r1Px);
    const [P1, P2, P3, P4, P5, P6] = pts;

    // T2 : rayon symbolique = distance P3P4 (px, arrondi entier)
    const r2Px = Math.round(dist(P3, P4));

    // T3 : diamètre symbolique [P5P6] → centre = milieu, rayon = dist/2
    const cx3 = Math.round((P5.x + P6.x) / 2);
    const cy3 = Math.round((P5.y + P6.y) / 2);
    const r3Px = Math.round(dist(P5, P6) / 2);

    const points = pts.map(p => ({ name: p.name, x: p.x, y: p.y }));
    const preShapes = [
        { type: 'line', from: P5.name, to: P6.name },
    ];

    const tasks = [
        {
            id: 'a',
            consigne: `Trace en <b>bleu</b> le cercle de centre ${P1.name} et de rayon ${r1Cm} cm.`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: P1.x, cy: P1.y, r: r1Px },
        },
        {
            id: 'b',
            consigne: `Trace en <b>rouge</b> le cercle de centre ${P2.name} et de rayon [${P3.name}${P4.name}].`,
            color: COLOR_ROUGE,
            shape: { type: 'circle', cx: P2.x, cy: P2.y, r: r2Px },
        },
        {
            id: 'c',
            consigne: `Trace en <b>vert</b> le cercle de diamètre [${P5.name}${P6.name}].`,
            color: COLOR_VERT,
            shape: { type: 'circle', cx: cx3, cy: cy3, r: r3Px },
        },
    ];

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : une phrase de CADRAGE, les consignes détaillées
    // restent portées par les tâches du composant de construction.
    const texte = '<p>Suis le programme de construction : trace les trois cercles demandés, chacun de la couleur indiquée.</p>';

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOLERANCE_PC,
            viewBox: VIEWBOX_PC,
            points,
            preShapes,
            tasks,
        },
        texte,
        opts(L_CONSTRUCTION),
    );
    payload._meta.generatorKey = 'construction_cercles_programme';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    vocabulaire_cercle_vf_figure:   genVocabulaireVFFigure,
    lexique_cercle_qcm:             genLexiqueCercleQcm,
    rayon_diametre_saisie:          genRayonDiametreSaisie,
    trou_vocabulaire_qcm:           genTrouVocabulaireQcm,
    construction_cercles_programme: genConstructionCerclesProgramme,
};

const FORMAT_LABELS = {
    qcm: 'QCM', qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', qcm_images: 'QCM images',
    saisie: 'Saisie', texte: 'Saisie',
    tableau_vf: 'Vrai/Faux', vf_figure: 'Vrai/Faux',
    programme: 'Construction', cercles_programme: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'vocabulaire_cercle', label: L_VOCAB_VF,       formatsList: buildFormats('vocabulaire_cercle') },
    { id: 'lexique_cercle',     label: L_LEXIQUE,        formatsList: buildFormats('lexique_cercle') },
    { id: 'rayon_diametre',     label: L_RAYON_DIAMETRE, formatsList: buildFormats('rayon_diametre') },
    { id: 'trou_vocabulaire',   label: L_TROU_VOCAB,     formatsList: buildFormats('trou_vocabulaire') },
    { id: 'construction',       label: L_CONSTRUCTION,   formatsList: buildFormats('construction') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genVocabulaireVFFigure,
    genLexiqueCercleQcm,
    genRayonDiametreSaisie,
    genTrouVocabulaireQcm,
    genConstructionCerclesProgramme,
};
